import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  CheckCheck,
  MessageCircleReply,
  MousePointerClick,
  ShoppingBag,
  Sparkles,
  Wifi,
  Send,
  ArrowUpRight,
  Activity,
  BadgeCheck,
  Bot,
  Loader2,
  PhoneCall,
} from "lucide-react";

/* ------------------------------------------------------------------
   Live Campaign Dashboard — polished v2
   Same layout, color palette, spacing & typography as before.
   This pass adds: cleaner header hierarchy, accent-tinted funnel
   with active-stage glow, value-first KPI cards, varied activity
   feed (Green Tick, AI lead routing), revenue pulse with delta
   badge, and a floating WhatsApp message bubble that morphs
   through one customer's full journey in sync with the counters.
------------------------------------------------------------------ */

const FALLBACK_CUSTOMERS = [
  { name: "Rahul Sharma", city: "Mumbai", color: "from-blue-500 to-indigo-500" },
  { name: "Priya Gupta", city: "Delhi", color: "from-rose-500 to-pink-500" },
  { name: "Ankit Verma", city: "Bangalore", color: "from-emerald-500 to-teal-500" },
  { name: "Sneha Jain", city: "Pune", color: "from-amber-500 to-orange-500" },
  { name: "Arjun Mehta", city: "Hyderabad", color: "from-violet-500 to-purple-500" },
  { name: "Kavya Nair", city: "Chennai", color: "from-cyan-500 to-blue-500" },
  { name: "Vikram Singh", city: "Jaipur", color: "from-fuchsia-500 to-pink-500" },
  { name: "Ishaan Patel", city: "Ahmedabad", color: "from-teal-500 to-emerald-500" },
  { name: "Meera Iyer", city: "Kochi", color: "from-orange-500 to-amber-500" },
  { name: "Karan Kapoor", city: "Chandigarh", color: "from-indigo-500 to-blue-500" },
];

const FALLBACK_REPLIES = [
  "When will my order arrive?",
  "Is COD available?",
  "Can I get the discount code?",
  "Do you ship to my city?",
  "Looks amazing! 😍",
];

const FALLBACK_CTAS = ["Shop Now", "Claim 50% OFF", "View Catalogue", "Grab Offer"];

const FALLBACK_MSG =
  "🛍️ Diwali Mega Sale is LIVE! Flat 50% OFF on everything. Use code DIWALI50.";

const FALLBACK_CAMPAIGN = {
  name: "Diwali Mega Sale 2026",
  id: "58433",
  template: "utility_diwali_v3",
  country: "+91 India",
  type: "Utility",
  audience: 50000,
  cost: "₹2,750",
  eta_seconds: 138,
  baseline_progress: 68,
};

const FALLBACK_COUNTERS = {
  sent: 50000,
  delivered: 49207,
  read: 40348,
  replied: 8472,
  clicked: 4841,
  ordered: 281,
  revenue: 845000,
};

const FALLBACK_DELTAS = {
  deliverability_delta: "+0.2%",
  read_rate_delta: "+0.4%",
  click_rate_delta: "+0.3%",
  roi_label: "vs cost",
};

const initials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2);

const formatINR = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
};

const formatNum = (n) => n.toLocaleString("en-IN");

/* Smoothly tween display value toward target */
const AnimatedNumber = ({ value, format = formatNum, className = "" }) => {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const dur = 600;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = from + (to - from) * eased;
      setDisplay(Number.isInteger(to) ? Math.round(v) : +v.toFixed(2));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return <span className={className}>{format(display)}</span>;
};

/* Stage configuration — accent palette per spec
   Delivered=Blue, Read=Emerald, Replied=Purple, CTA=Orange, Orders=Green */
const STAGE_META = {
  delivered: {
    label: "Delivered",
    icon: Check,
    iconColor: "text-blue-600",
    pillBg: "bg-blue-50",
    pillText: "text-blue-700",
    ring: "ring-blue-200/80",
    feedLabel: "Delivered",
  },
  read: {
    label: "Read",
    icon: CheckCheck,
    iconColor: "text-emerald-600",
    pillBg: "bg-emerald-50",
    pillText: "text-emerald-700",
    ring: "ring-emerald-200/80",
    feedLabel: "Read receipt",
  },
  replied: {
    label: "Replied",
    icon: MessageCircleReply,
    iconColor: "text-purple-600",
    pillBg: "bg-purple-50",
    pillText: "text-purple-700",
    ring: "ring-purple-200/80",
    feedLabel: "Replied",
  },
  clicked: {
    label: "CTA Clicked",
    icon: MousePointerClick,
    iconColor: "text-orange-600",
    pillBg: "bg-orange-50",
    pillText: "text-orange-700",
    ring: "ring-orange-200/80",
    feedLabel: "Clicked CTA",
  },
  ordered: {
    label: "Orders",
    icon: ShoppingBag,
    iconColor: "text-green-600",
    pillBg: "bg-green-50",
    pillText: "text-green-700",
    ring: "ring-green-200/80",
    feedLabel: "Order placed",
  },
  green_tick: {
    label: "Verified",
    icon: BadgeCheck,
    iconColor: "text-emerald-600",
    pillBg: "bg-emerald-50",
    pillText: "text-emerald-700",
    feedLabel: "Green Tick",
  },
  ai_lead: {
    label: "AI Lead",
    icon: Bot,
    iconColor: "text-violet-600",
    pillBg: "bg-violet-50",
    pillText: "text-violet-700",
    feedLabel: "AI assigned lead",
  },
};

/* ---------------- KPI mini-card (value-first hierarchy) ---------------- */
const KpiCard = ({ value, label, format, accent, delta }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 hover:border-slate-300 hover:shadow-[0_2px_8px_rgba(2,6,23,0.04)] transition-all">
    <AnimatedNumber
      value={value}
      format={format}
      className={`block text-xl font-extrabold tracking-tight leading-tight ${accent}`}
    />
    <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
      {label}
    </div>
    {delta && (
      <div className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600">
        <ArrowUpRight className="h-2.5 w-2.5" />
        {delta}
      </div>
    )}
  </div>
);

/* ---------------- Funnel stat with active-stage glow ---------------- */
const FunnelStat = ({ stage, value, isLast, accent, isActive }) => {
  const META = STAGE_META[stage] || {};
  const Icon = META.icon || Check;
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-[80px]">
      <motion.div
        animate={isActive ? { scale: [1, 1.035, 1] } : { scale: 1 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className={`flex-1 min-w-0 rounded-xl bg-white px-3 py-2.5 transition-all duration-300 ${
          isActive
            ? `border border-transparent ring-2 ${META.ring} shadow-[0_4px_14px_rgba(2,6,23,0.07)]`
            : "border border-slate-200"
        }`}
      >
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          <Icon className={`h-3 w-3 ${META.iconColor}`} />
          {META.label}
        </div>
        <AnimatedNumber
          value={value}
          format={formatNum}
          className={`mt-0.5 block text-base font-extrabold ${accent}`}
        />
      </motion.div>
      {!isLast && (
        <svg className="h-3 w-3 text-slate-300 flex-shrink-0" viewBox="0 0 12 12" fill="none">
          <path
            d="M3 2l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

/* ---------------- Activity feed row (stage + system events) ---------------- */
const FeedRow = ({ event }) => {
  if (event.kind === "system") {
    const META = STAGE_META[event.systemKind];
    const Icon = META.icon;
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 14, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
        className="flex items-center gap-3 px-3.5 py-2.5 border-b border-slate-100 last:border-b-0"
      >
        <div className={`h-8 w-8 rounded-full ${META.pillBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-3.5 w-3.5 ${META.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[13px] font-semibold text-slate-900 truncate">{event.title}</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${META.pillBg} ${META.pillText} flex items-center gap-1 flex-shrink-0`}>
              <Icon className="h-2.5 w-2.5" />
              {META.feedLabel}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 truncate mt-0.5">{event.detail}</div>
        </div>
        <span className="text-[10px] text-slate-400 flex-shrink-0">just now</span>
      </motion.div>
    );
  }

  const META = STAGE_META[event.stage];
  const Icon = META.icon;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 220, damping: 24 }}
      className="flex items-center gap-3 px-3.5 py-2.5 border-b border-slate-100 last:border-b-0"
    >
      <div
        className={`h-8 w-8 rounded-full bg-gradient-to-br ${event.customer.color} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
      >
        {initials(event.customer.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-slate-900 truncate">{event.customer.name}</span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${META.pillBg} ${META.pillText} flex items-center gap-1 flex-shrink-0`}>
            <Icon className="h-2.5 w-2.5" />
            {META.feedLabel}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">{event.detail}</div>
      </div>
      <span className="text-[10px] text-slate-400 flex-shrink-0">just now</span>
    </motion.div>
  );
};

/* ============================================================ */
const LiveCampaignDashboard = ({ content }) => {
  const campaign = { ...FALLBACK_CAMPAIGN, ...(content?.campaign || {}) };
  const initialCounters = { ...FALLBACK_COUNTERS, ...(content?.counters || {}), sent: campaign.audience ?? FALLBACK_COUNTERS.sent };
  const deltas = { ...FALLBACK_DELTAS, ...(content?.kpi_deltas || {}) };
  const customers = (content?.customers && content.customers.length) ? content.customers : FALLBACK_CUSTOMERS;
  const replies = (content?.reply_snippets && content.reply_snippets.length) ? content.reply_snippets : FALLBACK_REPLIES;
  const ctaLabels = (content?.cta_labels && content.cta_labels.length) ? content.cta_labels : FALLBACK_CTAS;
  const campaignMsg = content?.campaign_message || FALLBACK_MSG;

  // Baselines tuned to land near target KPIs
  const [counters, setCounters] = useState(initialCounters);
  const [progress, setProgress] = useState(campaign.baseline_progress ?? 68);
  const [timeRemaining, setTimeRemaining] = useState(campaign.eta_seconds ?? 138);

  const [feed, setFeed] = useState([]);
  const [activeStage, setActiveStage] = useState(null);
  const [revenuePulse, setRevenuePulse] = useState(null); // { id, amount }
  const [spotlight, setSpotlight] = useState(null); // { customer, stage, reply, amount, ctaLabel }

  const feedIdRef = useRef(0);
  const stageTimeoutRef = useRef(null);
  const pulseTimeoutRef = useRef(null);

  /* Push event to feed (capped at 5) */
  const pushFeed = (kind, payload) => {
    feedIdRef.current += 1;
    setFeed((prev) =>
      [{ id: feedIdRef.current, kind, ...payload }, ...prev].slice(0, 5)
    );
  };

  /* Briefly glow the funnel stage that just fired */
  const flashStage = (stage) => {
    setActiveStage(stage);
    clearTimeout(stageTimeoutRef.current);
    stageTimeoutRef.current = setTimeout(() => {
      setActiveStage((current) => (current === stage ? null : current));
    }, 900);
  };

  /* Fire a single funnel event — counter + feed + glow + (if order) pulse */
  const fireEvent = (stage, customer, detail, opts = {}) => {
    setCounters((c) => {
      const next = { ...c };
      if (stage === "ordered") {
        next.ordered = c.ordered + 1;
        next.revenue = c.revenue + (opts.amount || 0);
      } else {
        next[stage] = (c[stage] || 0) + 1;
      }
      return next;
    });
    pushFeed("stage", { stage, customer, detail });
    flashStage(stage);

    if (stage === "ordered") {
      const pulseId = Date.now() + Math.random();
      setRevenuePulse({ id: pulseId, amount: opts.amount });
      clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => {
        setRevenuePulse((p) => (p && p.id === pulseId ? null : p));
      }, 1400);
    }
  };

  /* --- Spotlight engine: walks one hero customer through every stage
         and drives the WhatsApp message bubble at the same time --- */
  useEffect(() => {
    let cancelled = false;
    const timers = [];

    const runSpotlight = () => {
      if (cancelled) return;
      const customer = customers[Math.floor(Math.random() * customers.length)];
      const reply = replies[Math.floor(Math.random() * replies.length)];
      const amount = 1499 + Math.floor(Math.random() * 3500); // ₹1,499 – ₹4,999
      const ctaLabel = ctaLabels[Math.floor(Math.random() * ctaLabels.length)];

      const schedule = [
        { stage: "sending", delay: 0 },
        { stage: "delivered", delay: 900 },
        { stage: "read", delay: 2200 },
        { stage: "replied", delay: 3500 },
        { stage: "clicked", delay: 4700 },
        { stage: "ordered", delay: 5800 },
      ];

      schedule.forEach((step) => {
        const t = setTimeout(() => {
          if (cancelled) return;
          setSpotlight({ customer, stage: step.stage, reply, amount, ctaLabel });
          if (step.stage !== "sending") {
            const detail =
              step.stage === "delivered"
                ? `Message delivered • ${customer.city}`
                : step.stage === "read"
                ? `Opened the campaign • ${customer.city}`
                : step.stage === "replied"
                ? `"${reply}"`
                : step.stage === "clicked"
                ? `Tapped "${ctaLabel}" — landed on offer page`
                : `Order confirmed • ${formatINR(amount)} • ${customer.city}`;
            fireEvent(
              step.stage,
              customer,
              detail,
              step.stage === "ordered" ? { amount } : {}
            );
          }
        }, step.delay);
        timers.push(t);
      });

      const dismissT = setTimeout(() => {
        if (!cancelled) setSpotlight(null);
      }, 7000);
      const nextT = setTimeout(runSpotlight, 8200);
      timers.push(dismissT, nextT);
    };

    const startT = setTimeout(runSpotlight, 600);
    timers.push(startT);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  /* --- Background engine: ambient delivered/read events + occasional
         Green Tick / AI lead system events to keep the feed alive --- */
  useEffect(() => {
    let cancelled = false;
    const tick = setInterval(() => {
      if (cancelled) return;
      const customer = customers[Math.floor(Math.random() * customers.length)];
      const r = Math.random();
      if (r < 0.07) {
        const isVerify = Math.random() < 0.5;
        if (isVerify) {
          pushFeed("system", {
            systemKind: "green_tick",
            title: "Green Tick Verified",
            detail: "WhatsApp Business profile approved by Meta",
          });
        } else {
          pushFeed("system", {
            systemKind: "ai_lead",
            title: "Lead routed to Sales",
            detail: `Sales Team ${["A", "B"][Math.floor(Math.random() * 2)]} • ${customer.city}`,
          });
        }
      } else if (r < 0.6) {
        fireEvent("delivered", customer, `Message delivered • ${customer.city}`);
      } else {
        fireEvent("read", customer, `Opened the campaign • ${customer.city}`);
      }
    }, 1900);

    return () => {
      cancelled = true;
      clearInterval(tick);
    };
  }, []);

  /* --- Progress + ETA loop --- */
  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => (p >= 96 ? 68 : +(p + 0.4).toFixed(1)));
      setTimeRemaining((t) => (t <= 30 ? 138 : t - 2));
    }, 1500);
    return () => clearInterval(id);
  }, []);

  /* --- Derived KPIs --- */
  const deliveryRate = (counters.delivered / counters.sent) * 100;
  const readRate = (counters.read / counters.delivered) * 100;
  const clickRate = (counters.clicked / counters.read) * 100;
  const roi = counters.revenue / 200000;

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}m ${sec.toString().padStart(2, "0")}s`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="bulk-campaign-preview"
      className="relative"
    >
      {/* Soft halo */}
      <div
        className="pointer-events-none absolute -inset-6 bg-gradient-to-br from-blue-100/40 via-transparent to-emerald-100/40 blur-2xl rounded-[2rem]"
        aria-hidden="true"
      />

      <div className="relative rounded-3xl bg-white border border-slate-200 shadow-[0_2px_4px_rgba(2,6,23,0.04),0_24px_60px_rgba(2,6,23,0.08)] overflow-hidden">
        {/* ─── Window chrome ─── */}
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            <span className="ml-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              Campaign Console · Live
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <Wifi className="h-3 w-3 text-emerald-500" />
            <span className="font-semibold">Connected</span>
          </div>
        </div>

        {/* ─── Header: cleaner two-tier hierarchy ─── */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {/* Primary row */}
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  className="text-[15px] font-extrabold text-slate-900 tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {campaign.name}
                </h3>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[10px] font-bold tracking-wide">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-60 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                  </span>
                  LIVE
                </span>
              </div>
              {/* Secondary row — smaller, muted */}
              <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10.5px] text-slate-400">
                <span>
                  <span className="text-slate-500">#</span>{campaign.id}
                </span>
                <span className="text-slate-300">·</span>
                <span>
                  <span className="text-slate-500">Template</span>{" "}
                  <span className="text-slate-600 font-medium">{campaign.template}</span>
                </span>
                <span className="text-slate-300">·</span>
                <span>{campaign.country}</span>
                <span className="text-slate-300">·</span>
                <span>{campaign.type}</span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Cost
              </div>
              <div className="text-sm font-extrabold text-slate-900">{campaign.cost}</div>
            </div>
          </div>

          {/* Progress strip — primary numbers stay prominent */}
          <div className="mt-3.5">
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Audience</span>
                <span className="font-bold text-slate-900">{formatNum(counters.sent)}</span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">Sending</span>
                <span className="font-bold text-slate-900">{progress.toFixed(0)}%</span>
              </div>
              <span className="text-slate-500">
                ETA <span className="font-bold text-slate-900">{fmtTime(timeRemaining)}</span>
              </span>
            </div>
            <div className="relative h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-blue-500 via-emerald-500 to-emerald-400"
                style={{ width: `${progress}%` }}
                transition={{ duration: 1.4, ease: "linear" }}
              />
              <motion.div
                className="absolute inset-y-0 w-16 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                animate={{ x: ["-20%", "120%"] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
                style={{ mixBlendMode: "overlay" }}
              />
            </div>
          </div>
        </div>

        {/* ─── Funnel row (accented + active-stage glow) ─── */}
        <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/60 to-white">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Send className="h-3 w-3 text-slate-500" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Realtime funnel
            </span>
          </div>
          <div className="flex items-stretch gap-1.5 overflow-x-auto no-scrollbar pb-1">
            <FunnelStat
              stage="delivered"
              value={counters.delivered}
              accent="text-blue-700"
              isActive={activeStage === "delivered"}
            />
            <FunnelStat
              stage="read"
              value={counters.read}
              accent="text-emerald-700"
              isActive={activeStage === "read"}
            />
            <FunnelStat
              stage="replied"
              value={counters.replied}
              accent="text-purple-700"
              isActive={activeStage === "replied"}
            />
            <FunnelStat
              stage="clicked"
              value={counters.clicked}
              accent="text-orange-700"
              isActive={activeStage === "clicked"}
            />
            <FunnelStat
              stage="ordered"
              value={counters.ordered}
              accent="text-green-700"
              isActive={activeStage === "ordered"}
              isLast
            />
          </div>
        </div>

        {/* ─── KPI grid (value-first hierarchy) ─── */}
        <div className="px-5 py-4 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <KpiCard
            value={deliveryRate}
            label="Deliverability"
            format={(n) => `${n.toFixed(1)}%`}
            accent="text-slate-900"
            delta={deltas.deliverability_delta}
          />
          <KpiCard
            value={readRate}
            label="Read rate"
            format={(n) => `${n.toFixed(1)}%`}
            accent="text-emerald-700"
            delta={deltas.read_rate_delta}
          />
          <KpiCard
            value={clickRate}
            label="Click rate"
            format={(n) => `${n.toFixed(1)}%`}
            accent="text-orange-700"
            delta={deltas.click_rate_delta}
          />
          <KpiCard
            value={roi}
            label="ROI"
            format={(n) => `${n.toFixed(1)}×`}
            accent="text-green-700"
            delta={deltas.roi_label}
          />
        </div>

        {/* ─── Live activity feed ─── */}
        <div>
          <div className="px-5 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Activity className="h-3 w-3 text-emerald-500" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Live activity
              </span>
            </div>
            <span className="text-[10px] text-slate-400">updated just now</span>
          </div>
          <div className="min-h-[200px] max-h-[200px] overflow-hidden relative">
            <AnimatePresence initial={false}>
              {feed.map((evt) => (
                <FeedRow key={evt.id} event={evt} />
              ))}
            </AnimatePresence>
            {feed.length === 0 && (
              <div className="px-5 py-6 text-center text-xs text-slate-400">
                Listening for events…
              </div>
            )}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent" />
          </div>
        </div>
      </div>

      {/* ─── Floating Revenue chip with pulse + delta badge ─── */}
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.9 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, type: "spring", stiffness: 220, damping: 20 }}
        animate={revenuePulse ? { scale: [1, 1.04, 1] } : { scale: 1 }}
        data-testid="bulk-floating-roi"
        className="absolute -bottom-5 -right-5 sm:-right-6 rounded-2xl bg-white border border-slate-200 shadow-[0_4px_8px_rgba(2,6,23,0.04),0_20px_40px_rgba(2,6,23,0.12)] px-4 py-3 flex items-center gap-3"
      >
        <div className="relative h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
          <Sparkles className="h-4 w-4 text-white" />
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">
            Revenue
          </div>
          <div className="flex items-baseline gap-1.5">
            <AnimatedNumber
              value={counters.revenue}
              format={formatINR}
              className="text-base font-extrabold text-slate-900 tracking-tight"
            />
            <span className="text-[10px] font-bold text-emerald-600">{roi.toFixed(1)}× ROI</span>
          </div>
        </div>
        {/* +₹delta badge */}
        <AnimatePresence>
          {revenuePulse && (
            <motion.div
              key={revenuePulse.id}
              initial={{ opacity: 0, y: 4, scale: 0.9 }}
              animate={{ opacity: 1, y: -10, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.95 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="absolute -top-2 right-3 px-1.5 py-0.5 rounded-md bg-emerald-500 text-white text-[10px] font-extrabold shadow-md"
            >
              +{formatINR(revenuePulse.amount)}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ─── Floating WhatsApp bubble — morphs through one customer's journey ─── */}
      <AnimatePresence>
        {spotlight && (
          <motion.div
            key="wa-bubble"
            initial={{ opacity: 0, x: -12, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: -10, y: -8, scale: 0.96 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            data-testid="bulk-whatsapp-bubble"
            className="hidden sm:block absolute -top-5 -left-4 lg:-left-6 w-[244px] z-10"
          >
            <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_4px_10px_rgba(2,6,23,0.06),0_22px_50px_rgba(2,6,23,0.14)] overflow-hidden">
              {/* WhatsApp header */}
              <div className="px-3 py-2 bg-[#075e54] flex items-center gap-2">
                <div
                  className={`h-7 w-7 rounded-full bg-gradient-to-br ${spotlight.customer.color} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
                >
                  {initials(spotlight.customer.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-white truncate">
                    {spotlight.customer.name}
                  </div>
                  <div className="text-[9px] text-emerald-200 truncate">
                    {spotlight.customer.city} · online
                  </div>
                </div>
                <PhoneCall className="h-3 w-3 text-emerald-200 flex-shrink-0" />
              </div>

              {/* Chat body */}
              <div className="p-2.5 bg-[#e5ddd5]/70 space-y-1.5 min-h-[88px]">
                {/* Outgoing campaign message */}
                <div className="ml-auto max-w-[88%] rounded-lg rounded-tr-none bg-[#dcf8c6] px-2.5 py-1.5 shadow-sm">
                  <div className="text-[10.5px] text-slate-800 leading-snug">
                    {campaignMsg}
                  </div>
                  <div className="mt-0.5 flex items-center justify-end gap-1 text-[9px] text-slate-500">
                    <span>12:43</span>
                    {spotlight.stage === "sending" && (
                      <Loader2 className="h-2.5 w-2.5 animate-spin text-slate-400" />
                    )}
                    {spotlight.stage === "delivered" && (
                      <Check className="h-3 w-3 text-slate-500" />
                    )}
                    {(spotlight.stage === "read" ||
                      spotlight.stage === "replied" ||
                      spotlight.stage === "clicked" ||
                      spotlight.stage === "ordered") && (
                      <CheckCheck className="h-3 w-3 text-blue-500" />
                    )}
                  </div>
                </div>

                {/* Customer reply */}
                {(spotlight.stage === "replied" ||
                  spotlight.stage === "clicked" ||
                  spotlight.stage === "ordered") && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mr-auto max-w-[80%] rounded-lg rounded-tl-none bg-white px-2.5 py-1.5 shadow-sm"
                  >
                    <div className="text-[10.5px] text-slate-800">{spotlight.reply}</div>
                    <div className="mt-0.5 text-[9px] text-slate-400 text-right">12:44</div>
                  </motion.div>
                )}

                {/* CTA tap indicator */}
                {(spotlight.stage === "clicked" || spotlight.stage === "ordered") && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="ml-auto max-w-[88%] rounded-md bg-white/85 border border-orange-100 px-2 py-1 text-[10px] font-semibold text-orange-700 flex items-center gap-1"
                  >
                    <MousePointerClick className="h-2.5 w-2.5" />
                    Tapped “{spotlight.ctaLabel}” →
                  </motion.div>
                )}

                {/* Order confirmation banner */}
                {spotlight.stage === "ordered" && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 280, damping: 18 }}
                    className="ml-auto max-w-[88%] rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 px-2.5 py-1.5 text-white shadow-md"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-bold">
                      <ShoppingBag className="h-3 w-3" /> Order confirmed
                    </div>
                    <div className="mt-0.5 text-[11px] font-extrabold">
                      {formatINR(spotlight.amount)}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Status footer */}
              <div className="px-3 py-1.5 bg-white border-t border-slate-100 flex items-center gap-1.5">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Status
                </span>
                <motion.span
                  key={spotlight.stage}
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className="text-[10.5px] font-bold text-slate-700"
                >
                  {spotlight.stage === "sending" && "Sending…"}
                  {spotlight.stage === "delivered" && "✓ Delivered"}
                  {spotlight.stage === "read" && "✓✓ Read"}
                  {spotlight.stage === "replied" && "💬 Customer replied"}
                  {spotlight.stage === "clicked" && "🛒 CTA tapped"}
                  {spotlight.stage === "ordered" && "📦 Order confirmed"}
                </motion.span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LiveCampaignDashboard;
